import { useState, useCallback } from "react";
import { InstagramDiscoveryService } from "../services/instagram-discovery";
import { InstagramSelectionAccount } from "../types";
import { toast as sonnerToast } from "sonner";

interface InstagramDiscoveryState {
  isLoadingAccounts: boolean;
  isSelecting: boolean;
  availableAccounts: InstagramSelectionAccount[];
  selectedAccountId: string | null;
  error: string | null;
}

export function useInstagramDiscovery() {
  const [state, setState] = useState<InstagramDiscoveryState>({
    isLoadingAccounts: false,
    isSelecting: false,
    availableAccounts: [],
    selectedAccountId: null,
    error: null
  });

  const updateState = useCallback((updates: Partial<InstagramDiscoveryState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const fetchAvailableAccounts = useCallback(async () => {
    updateState({ isLoadingAccounts: true, error: null });

    const result = await InstagramDiscoveryService.fetchAvailableInstagramAccounts();

    if (result.success) {
      updateState({
        availableAccounts: result.accounts || [],
        isLoadingAccounts: false
      });
      return result;
    } else {
      updateState({
        error: result.error || 'Failed to fetch Instagram accounts',
        isLoadingAccounts: false
      });
      sonnerToast.error(result.error || 'Failed to fetch Instagram accounts');
      return result;
    }
  }, [updateState]);

  const selectAccount = useCallback(async (
    accountId: string, 
    facebookConnectionId: string
  ) => {
    updateState({ isSelecting: true, error: null });

    const result = await InstagramDiscoveryService.selectInstagramAccount(
      accountId, 
      facebookConnectionId
    );

    if (result.success) {
      updateState({
        selectedAccountId: accountId,
        isSelecting: false
      });
      sonnerToast.success("Instagram account selected successfully!");
    } else {
      updateState({
        error: result.error || 'Failed to select Instagram account',
        isSelecting: false
      });
      sonnerToast.error(result.error || 'Failed to select Instagram account');
    }

    return result;
  }, [updateState]);

  const reset = useCallback(() => {
    setState({
      isLoadingAccounts: false,
      isSelecting: false,
      availableAccounts: [],
      selectedAccountId: null,
      error: null
    });
  }, []);

  return {
    ...state,
    fetchAvailableAccounts,
    selectAccount,
    reset
  };
} 